import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../config/supabaseConfig";
import ApontamentoVolume, { useVolumePorAluno } from "./ApontamentoVolume";
import "./AlunoFullScreen.css";

const iniciais = (nome = "") =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

const temLesao = (aluno) => {
  const l = (aluno?.lesao || "").toLowerCase();
  return l.includes("sim") || l.includes("moderada") || l.includes("grave");
};

// Severidade (feedback Tales 08/07): mesmos valores do cadastro que a Sala
// usa — "Sim - Lesao Grave" → vermelho; qualquer outra lesão → amarelo.
const lesaoGrave = (aluno) =>
  (aluno?.lesao || "").toLowerCase().includes("grave");

// Rótulo do tipo de avaliação (Task B2) — mesmos valores do CHECK da tabela
// `avaliacoes` (inicial/reavaliacao/postural/nutricional).
const tipoAvaliacaoLabel = {
  inicial: "Avaliação inicial",
  reavaliacao: "Reavaliação",
  postural: "Avaliação postural",
  nutricional: "Avaliação nutricional",
};

const AlunoFullScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?from=sala&aula=<id>: aberto de dentro da aula real — "voltar" vai pra
  // /sala e o carrossel navega entre os alunos daquela aula. Sem os params,
  // comporta como no showcase /novo (volta pro /novo/sala, sem carrossel).
  const daSala = searchParams.get("from") === "sala";
  const aulaId = searchParams.get("aula");
  const rotaVoltar = daSala ? "/sala" : "/novo/sala";
  // returnTo (feedback Tales 08/07): Editar cargas/cadastro abertos DAQUI
  // voltam pra esta full screen, com aluno e contexto de aula preservados.
  const meuPath = `/aluno/${id}${
    daSala ? `?from=sala${aulaId ? `&aula=${aulaId}` : ""}` : ""
  }`;
  const paramReturnTo = `?returnTo=${encodeURIComponent(meuPath)}`;

  const [aluno, setAluno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  // Carrossel: ids dos alunos da mesma aula (ordem de entrada na aula).
  const [idsAula, setIdsAula] = useState([]);
  // Volume por grupamento — persiste em aluno_volume (Fase 3, 10/07), mesmo
  // hook usado na Sala. Carrega só o aluno desta tela.
  const {
    getValores: getValoresVolume,
    getMetas: getMetasVolume,
    bumpValor: bumpValorVolume,
  } = useVolumePorAluno(id ? [id] : []);
  // Contador de aulas (feedback Tales 08/07) — mesma fonte do histórico:
  // RPC aulas_realizadas_por_aluno (regra §2, só finalizada/realizada).
  const [totalAulas, setTotalAulas] = useState(null);
  // Referência de carga (Task A2/full screen) — lê `aluno_cargas` direto,
  // mesmo padrão de CargasAluno.jsx (join com exercicios pra pegar o nome).
  const [cargas, setCargas] = useState([]);
  // Próxima avaliação (Task B2) — select direto em `avaliacoes` (mesma
  // regra da RPC proxima_avaliacao_por_aluno, mas aqui só 1 aluno): sem
  // data_realizada, data_agendada futura, a mais próxima primeiro.
  const [proximaAvaliacao, setProximaAvaliacao] = useState(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const { data, error } = await supabase.rpc("aulas_realizadas_por_aluno");
      if (error) {
        console.error("Erro ao carregar contador de aulas:", error);
        return;
      }
      if (ativo) {
        const linha = (data || []).find((r) => r.aluno_id === id);
        setTotalAulas(linha ? Number(linha.total) : 0);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("alunos")
          .select("*")
          .eq("id", id)
          .single();
        if (error) throw error;
        if (ativo) setAluno(data);
      } catch (e) {
        console.error("Erro ao carregar aluno:", e);
        if (ativo) setErro(e.message || "Falha ao carregar aluno.");
      } finally {
        if (ativo) setLoading(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const { data, error } = await supabase
        .from("aluno_cargas")
        .select("carga_maxima, exercicios(nome)")
        .eq("aluno_id", id)
        .order("carga_maxima", { ascending: false });
      if (error) {
        console.error("Erro ao carregar cargas do aluno:", error);
        return;
      }
      if (ativo) setCargas(data || []);
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const hoje = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from("avaliacoes")
        .select("tipo, data_agendada")
        .eq("aluno_id", id)
        .is("data_realizada", null)
        .gte("data_agendada", hoje)
        .order("data_agendada", { ascending: true })
        .limit(1);
      if (error) {
        console.error("Erro ao carregar próxima avaliação:", error);
        return;
      }
      if (ativo) setProximaAvaliacao((data && data[0]) || null);
    })();
    return () => {
      ativo = false;
    };
  }, [id]);

  useEffect(() => {
    if (!aulaId) return;
    let ativo = true;
    (async () => {
      // Select direto em aula_alunos (NÃO usar o embed alunos(*) do
      // service — colide com a coluna jsonb de aulas, ver Presala).
      const { data, error } = await supabase
        .from("aula_alunos")
        .select("aluno_id")
        .eq("aula_id", aulaId);
      if (!error && ativo && Array.isArray(data)) {
        setIdsAula(data.map((r) => r.aluno_id));
      }
    })();
    return () => {
      ativo = false;
    };
  }, [aulaId]);

  const posicao = idsAula.indexOf(id);
  const temCarrossel = daSala && idsAula.length > 1 && posicao !== -1;

  const irPara = useCallback(
    (delta) => {
      if (!temCarrossel) return;
      const destino =
        idsAula[(posicao + delta + idsAula.length) % idsAula.length];
      navigate(`/aluno/${destino}?from=sala&aula=${aulaId}`);
    },
    [temCarrossel, idsAula, posicao, aulaId, navigate]
  );

  useEffect(() => {
    if (!temCarrossel) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") irPara(-1);
      if (e.key === "ArrowRight") irPara(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [temCarrossel, irPara]);

  // Swipe entre alunos (tablet). Só decide no touchend, comparando dx/dy,
  // pra não brigar com o scroll vertical nem com os steppers de volume.
  const toqueInicio = useRef(null);
  const onTouchStart = (e) => {
    if (!temCarrossel) return;
    const t = e.touches[0];
    toqueInicio.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!temCarrossel || !toqueInicio.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - toqueInicio.current.x;
    const dy = t.clientY - toqueInicio.current.y;
    toqueInicio.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      irPara(dx < 0 ? 1 : -1);
    }
  };

  if (loading) {
    return (
      <div className="ihfs">
        <div className="state">Carregando aluno…</div>
      </div>
    );
  }

  if (erro || !aluno) {
    return (
      <div className="ihfs">
        <div className="content">
          <button className="back" onClick={() => navigate(rotaVoltar)}>
            ← voltar à sala
          </button>
          <div className="state">{erro || "Aluno não encontrado."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ihfs" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="topbar">
        <div className="brand">
          <span className="sub">Sala · Full screen do aluno</span>
        </div>
        {temCarrossel ? (
          <div className="ctx carrossel">
            <button className="nav-aluno" onClick={() => irPara(-1)}>
              ← anterior
            </button>
            <span className="pos">
              {posicao + 1} de {idsAula.length}
            </span>
            <button className="nav-aluno" onClick={() => irPara(1)}>
              próximo →
            </button>
          </div>
        ) : (
          !daSala && (
            <div className="ctx">
              <span>Aula 16:00–17:00</span>
              <span className="badge badge-green">Aberto há 04:12</span>
            </div>
          )
        )}
      </div>

      <div className="content">
        <button className="back" onClick={() => navigate(rotaVoltar)}>
          ← voltar à sala
        </button>

        {/* Hero + ações */}
        <div
          className="row between center mt16"
          style={{ flexWrap: "wrap", gap: 16 }}
        >
          <div className="hero">
            <div className="avatar">{iniciais(aluno.nome)}</div>
            <div>
              <h1>{aluno.nome}</h1>
              <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                {aluno.plano && <span className="badge badge-green">{aluno.plano}</span>}
                {aluno.nivel && <span className="badge badge-iron">{aluno.nivel}</span>}
                {temLesao(aluno) && (
                  <span
                    className={`badge ${
                      lesaoGrave(aluno) ? "badge-red" : "badge-warn"
                    }`}
                  >
                    Lesão / restrição
                  </span>
                )}
                {/* verde como o badge do plano (pedido Rodrigo 10/07) */}
                <span className="badge badge-green">
                  {totalAulas === null ? "— aulas" : `${totalAulas} aulas`}
                </span>
              </div>
            </div>
          </div>
          <div className="actions">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() =>
                navigate(`/detalhe-aluno/${aluno.id}${daSala ? "?from=sala" : ""}`)
              }
            >
              ⟲ Histórico
            </button>
            <button
              className="btn btn-dark btn-sm"
              onClick={() => navigate(`/cargas/${aluno.id}${paramReturnTo}`)}
            >
              ⚙ Editar Cargas
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() =>
                navigate(`/editar-aluno/${aluno.id}${paramReturnTo}`)
              }
            >
              ✎ Editar Cadastro
            </button>
          </div>
        </div>

        <div className="lock-note mt16">
          🔒 Modo visualização — o treino fica bloqueado para leitura em aula. Use os
          botões acima para editar.
        </div>

        {/* Grid principal */}
        <div className="aluno-grid mt24">
          <div className="card">
            {/* Feedback Tales 08/07: bloco `objetivo` volta a ser "Objetivo
                de treino" (verde, não vermelho) — reverte a rodada anterior. */}
            <div className="section-title" style={{ color: "var(--color-green)" }}>
              Objetivo de treino
            </div>
            <p className="texto-livre">
              {aluno.objetivo || "Nenhum objetivo de treino registrado."}
            </p>

            <div className="section-title mt24">Treino</div>
            <div className="box-treino">
              <p className="texto-livre muted">
                {aluno.observacoes?.trim() ||
                  (temLesao(aluno)
                    ? `⚠ ${aluno.lesao}${
                        aluno.tipo_lesao ? ` — ${aluno.tipo_lesao}` : ""
                      }`
                    : "Sem observações.")}
              </p>
            </div>

            {/* Campo novo do cadastro (A0.3) — espelho logo abaixo de Treino. */}
            <div className="section-title mt24">Exercícios obrigatórios</div>
            <p className="texto-livre">
              {aluno.exercicios_obrigatorios?.trim() ||
                "Nenhum exercício obrigatório registrado."}
            </p>
          </div>

          <div>
            <div className="card">
              <div className="section-title">Dados do aluno</div>
              <div className="kv">
                <span className="k">Plano</span>
                <span className="v">{aluno.plano || "—"}</span>
              </div>
              <div className="kv">
                <span className="k">Nível</span>
                <span className="v">{aluno.nivel || "—"}</span>
              </div>
              <div className="kv">
                <span className="k">Idade</span>
                <span className="v">{aluno.idade ? `${aluno.idade} anos` : "—"}</span>
              </div>
              <div className="kv">
                <span className="k">Lesão / restrição</span>
                <span
                  className={`v ${
                    temLesao(aluno)
                      ? lesaoGrave(aluno)
                        ? "v-lesao-grave"
                        : "v-lesao-moderada"
                      : ""
                  }`}
                >
                  {temLesao(aluno) ? aluno.tipo_lesao || aluno.lesao : "Nenhuma"}
                </span>
              </div>
              <div className="kv">
                <span className="k">Aulas realizadas</span>
                <span className="v">{totalAulas ?? "—"}</span>
              </div>
              <div className="kv">
                <span className="k">Telefone</span>
                <span className="v">{aluno.telefone?.trim() || "—"}</span>
              </div>
            </div>

            <div className="card mt16">
              <div className="section-title">Próxima avaliação</div>
              {proximaAvaliacao ? (
                <div className="row between center">
                  <div>
                    <b>
                      {tipoAvaliacaoLabel[proximaAvaliacao.tipo] ||
                        "Avaliação"}
                    </b>
                    <div className="tag-note">conforme plano</div>
                  </div>
                  <span className="badge badge-warn">
                    {new Date(
                      `${proximaAvaliacao.data_agendada}T12:00:00`
                    ).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              ) : (
                <div className="row between center">
                  <span className="v">—</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Apontamento de séries por grupamento (mesmo componente da Sala;
            persiste em aluno_volume — Fase 3, 10/07) */}
        <div className="card mt24">
          <div className="section-title">Apontamento de séries · esta aula</div>
          <div className="mt16">
            <ApontamentoVolume
              valores={getValoresVolume(id)}
              metas={getMetasVolume(id)}
              onChange={(sigla, delta) => bumpValorVolume(id, sigla, delta)}
            />
          </div>
        </div>

        {/* Referência de carga */}
        <div className="card mt16">
          <div className="row between center">
            <div className="section-title" style={{ marginBottom: 0 }}>
              Referência de carga
            </div>
            <button
              className="btn btn-dark btn-sm"
              onClick={() => navigate(`/cargas/${aluno.id}${paramReturnTo}`)}
            >
              ⚙ Editar Cargas
            </button>
          </div>
          {cargas.length ? (
            <table className="data mt16" style={{ fontSize: ".86rem" }}>
              <thead>
                <tr>
                  <th>Exercício</th>
                  <th>Máx</th>
                  <th>Alta 90%</th>
                  <th>Média 75%</th>
                  <th>Baixa 65%</th>
                </tr>
              </thead>
              <tbody>
                {cargas.map((c, i) => (
                  <tr key={i}>
                    <td>{c.exercicios?.nome || "—"}</td>
                    <td>{c.carga_maxima}kg</td>
                    <td>{Math.round(c.carga_maxima * 0.9)}kg</td>
                    <td>{Math.round(c.carga_maxima * 0.75)}kg</td>
                    <td>{Math.round(c.carga_maxima * 0.65)}kg</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="texto-livre muted mt16">Nenhuma carga lançada.</p>
          )}
        </div>

        <div className="row" style={{ justifyContent: "flex-end", marginTop: 20 }}>
          {temCarrossel && (
            <button className="btn btn-dark" onClick={() => irPara(1)}>
              Próximo aluno →
            </button>
          )}
          <button className="btn btn-ghost" onClick={() => navigate(rotaVoltar)}>
            Voltar à sala
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlunoFullScreen;
